import {
  supabase, uid,
  isRecord, toStringValue, toUnknownList, normalizeStringList,
} from './shared'
import type { RecordLike } from './shared'
import type { Lorebook, LorebookEntry } from '@/types/domain'
import type { LorebookCreateData, LorebookUpdateData } from '../../contract'
import type { LorebooksListResponse, SingleLorebookResponse } from '@/types/api'

const LOREBOOK_ICON_VALUES = new Set([
  'book-open-variant', 'account-outline', 'map-outline', 'castle',
  'sword-cross', 'pine-tree', 'star-four-points-outline', 'flask-outline',
]);
const LOREBOOK_COLOR_VALUES = new Set(['slate', 'blue', 'green', 'amber', 'rose', 'teal']);

export function normalizeLorebookIcon(value: string): string {
  return LOREBOOK_ICON_VALUES.has(value) ? value : 'book-open-variant';
}

export function normalizeLorebookColor(value: string): string {
  return LOREBOOK_COLOR_VALUES.has(value) ? value : 'slate';
}

export function normalizeLorebookIdList(values: unknown[] = []): number[] {
  const seen = new Set<number>();
  return (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

// Accepts untyped input: entries arrive both as typed LorebookEntryData and as
// raw creator draft-payload records.
export function normalizeLorebookEntryInput(entryInput: unknown, position = 0) {
  const entry: RecordLike = isRecord(entryInput) ? entryInput : {};
  return {
    position,
    name: entry.name?.toString?.().trim?.() || `Entry ${position + 1}`,
    content: entry.content?.toString?.() ?? '',
    enabled: entry.enabled !== false,
    triggers: normalizeStringList(toUnknownList(entry.triggers)),
    min_messages:
      entry.minMessages == null || entry.minMessages === ''
        ? null
        : Math.max(0, Number(entry.minMessages) || 0),
    placement: entry.placement === 'scenario' ? 'scenario' : 'default',
    priority: Number.isFinite(Number(entry.priority)) ? Number(entry.priority) : 0,
    tags: normalizeStringList(toUnknownList(entry.tags)),
    match_scope:
      entry.matchScope === 'user' || entry.matchScope === 'character' ? entry.matchScope : 'both',
    advanced:
      entry.advanced && typeof entry.advanced === 'object' && !Array.isArray(entry.advanced)
        ? entry.advanced
        : {},
  };
}

export function sortLorebookEntries(entries: RecordLike[] = []) {
  return [...entries].sort((a, b) => {
    const byPosition = Number(a?.position ?? 0) - Number(b?.position ?? 0);
    if (byPosition) return byPosition;
    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
}

function mapLorebookEntryRow(row: unknown): LorebookEntry {
  const safeRow = isRecord(row) ? row : {};
  const minMessages = safeRow.min_messages ?? safeRow.minMessages
  const matchScope = safeRow.match_scope ?? safeRow.matchScope
  return {
    id: Number(safeRow.id) || 0,
    lorebook_id: Number(safeRow.lorebook_id) || 0,
    position: Number(safeRow.position ?? 0) || 0,
    name: toStringValue(safeRow.name),
    content: toStringValue(safeRow.content),
    enabled: safeRow.enabled !== false,
    triggers: normalizeStringList(toUnknownList(safeRow.triggers)),
    minMessages: minMessages == null ? null : Number(minMessages),
    placement: safeRow.placement === 'scenario' ? 'scenario' : 'default',
    priority: Number.isFinite(Number(safeRow.priority)) ? Number(safeRow.priority) : 0,
    tags: normalizeStringList(toUnknownList(safeRow.tags)),
    matchScope:
      matchScope === 'user' || matchScope === 'character' ? matchScope : 'both',
    advanced: isRecord(safeRow.advanced) ? safeRow.advanced : {},
    created_at: toStringValue(safeRow.created_at),
    updated_at: toStringValue(safeRow.updated_at),
  };
}

function mapLorebookRow(row: unknown, entryRows: unknown[] = []): Lorebook {
  const safeRow = isRecord(row) ? row : {};
  const lorebookId = Number(safeRow.id) || 0;
  const safeEntryRows = sortLorebookEntries(toUnknownList(entryRows).filter(isRecord));
  return {
    id: lorebookId,
    user_id: toStringValue(safeRow.user_id),
    name: toStringValue(safeRow.name),
    description: toStringValue(safeRow.description),
    icon: normalizeLorebookIcon(toStringValue(safeRow.icon)),
    color: normalizeLorebookColor(toStringValue(safeRow.color)),
    version: 1,
    entries: safeEntryRows
      .filter((entry) => Number(entry.lorebook_id) === lorebookId)
      .map(mapLorebookEntryRow),
    created_at: toStringValue(safeRow.created_at),
    updated_at: toStringValue(safeRow.updated_at),
  };
}

/** RPC character projections embed app-shaped entries instead of separate rows. */
export function mapEmbeddedLorebook(row: RecordLike): Lorebook {
  const id = Number(row.id) || 0
  const entries = toUnknownList(row.entries).filter(isRecord)
    .map((entry) => ({ ...entry, lorebook_id: entry.lorebook_id ?? id }))
  return mapLorebookRow(row, entries)
}

function mapLorebooksWithEntries(lorebookRows: unknown[] = [], entryRows: unknown[] = []) {
  const entriesByLorebookId = new Map<number, unknown[]>();

  for (const row of toUnknownList(entryRows).filter(isRecord)) {
    const lorebookId = Number(row.lorebook_id);
    if (!entriesByLorebookId.has(lorebookId)) {
      entriesByLorebookId.set(lorebookId, []);
    }
    entriesByLorebookId.get(lorebookId)!.push(row);
  }

  return toUnknownList(lorebookRows).map((row) =>
    mapLorebookRow(row, isRecord(row) ? entriesByLorebookId.get(Number(row.id)) || [] : []),
  );
}

async function getLorebookEntriesByLorebookIds(lorebookIds: unknown[] = []) {
  const ids = Array.from(new Set(toUnknownList(lorebookIds).map((value) => Number(value)).filter(Number.isFinite)));
  if (!ids.length) return [];

  const { data, error } = await supabase
    .from('lorebook_entries')
    .select('*')
    .in('lorebook_id', ids);

  if (error) throw error;
  return sortLorebookEntries(toUnknownList(data).filter(isRecord));
}

async function touchLorebookUpdatedAt(lorebookId: number | string) {
  const { error } = await supabase
    .from('lorebooks')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', lorebookId);

  if (error) throw error;
}

async function replaceLorebookEntries(lorebookId: number | string, entries: unknown[] = []) {
  const { error: deleteError } = await supabase
    .from('lorebook_entries')
    .delete()
    .eq('lorebook_id', lorebookId);

  if (deleteError) throw deleteError;

  const normalizedEntries = toUnknownList(entries).map((entry, index) => ({
    lorebook_id: lorebookId,
    ...normalizeLorebookEntryInput(entry, index),
  }));

  if (normalizedEntries.length) {
    const { error: insertError } = await supabase
      .from('lorebook_entries')
      .insert(normalizedEntries);

    if (insertError) throw insertError;
  }

  await touchLorebookUpdatedAt(lorebookId);
}

export async function getLorebooks(): Promise<LorebooksListResponse> {
  const userId = await uid();
  const { data, error } = await supabase
    .from('lorebooks')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) throw error;

  const lorebooks = toUnknownList(data);
  const entryRows = await getLorebookEntriesByLorebookIds(lorebooks.map((row) => (isRecord(row) ? row.id : undefined)));
  return {
    lorebooks: mapLorebooksWithEntries(lorebooks, entryRows),
  };
}

export async function getLorebook(id: number | string): Promise<SingleLorebookResponse> {
  const userId = await uid();
  const { data, error } = await supabase
    .from('lorebooks')
    .select('*')
    .eq('user_id', userId)
    .eq('id', id)
    .single();

  if (error) throw error;

  const entryRows = await getLorebookEntriesByLorebookIds([data.id]);
  return {
    lorebook: mapLorebookRow(data, entryRows),
  };
}

export async function createLorebook(lorebookData: LorebookCreateData): Promise<SingleLorebookResponse> {
  const userId = await uid();
  const { entries, ...fields } = lorebookData;
  const payload = {
    user_id: userId,
    name: fields.name.trim() || 'Untitled Lorebook',
    description: fields.description ?? '',
    icon: normalizeLorebookIcon(fields.icon ?? ''),
    color: normalizeLorebookColor(fields.color ?? ''),
    version: 1,
  };

  const { data, error } = await supabase
    .from('lorebooks')
    .insert(payload)
    .select('*')
    .single();

  if (error) throw error;

  await replaceLorebookEntries(data.id, entries ?? []);
  return getLorebook(data.id);
}

export async function updateLorebook(id: number | string, lorebookData: LorebookUpdateData = {}): Promise<SingleLorebookResponse> {
  const userId = await uid();
  const { entries, ...fields } = lorebookData;
  const payload: RecordLike = {};

  if (fields.name !== undefined) {
    payload.name = fields.name.trim() || 'Untitled Lorebook';
  }
  if (fields.description !== undefined) {
    payload.description = fields.description;
  }
  if (fields.icon !== undefined) {
    payload.icon = normalizeLorebookIcon(fields.icon);
  }
  if (fields.color !== undefined) {
    payload.color = normalizeLorebookColor(fields.color);
  }
  if (fields.version !== undefined) {
    payload.version = 1;
  }

  if (Object.keys(payload).length > 0) {
    const { error } = await supabase
      .from('lorebooks')
      .update(payload)
      .eq('user_id', userId)
      .eq('id', id);

    if (error) throw error;
  }

  if (entries !== undefined) {
    await replaceLorebookEntries(id, entries);
  }

  return getLorebook(id);
}

export async function deleteLorebook(id: number | string) {
  const userId = await uid();
  const { error } = await supabase
    .from('lorebooks')
    .delete()
    .eq('user_id', userId)
    .eq('id', id);

  if (error) throw error;
  return { message: 'Lorebook deleted successfully' };
}
